import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { TextareaAutosize } from "@/components/ui/textarea-autosize";
import { Button } from "@/components/ui/button";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useModal } from "@/stores/use-modal-store";
import { Text } from "@/components/ui/text";
import { Checkbox } from "@/components/ui/checkbox";
import { useEventQnASettings } from "@/hooks/use-qna-query";
import eventClientApi from "@/api-client/event-client";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Input } from "@/components/ui/input";
import dayjs from "dayjs";
import { Calendar, Calendar as DatePicker } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select";
import { getendTimeOpions, getStartTimeOpions } from "@/lib/utils";
import Divider from "@/components/ui/divider";
import TimezoneSelectWithSearch from "@/components/app/events/create-event/TimezoneWithSearch";
import { useTimezones } from "@/hooks/use-timezones";
import { useRouter } from "next/navigation";
import { toast } from "sonner";
import { useDevice } from "@/hooks/use-device";

const DuplicateEventSchema = Yup.object().shape({
  startDate: Yup.date().required("Start date is required"),
  endDate: Yup.date()
    .required("End date is required")
    .min(Yup.ref("startDate"), "End date cannot be before start date"),
  startTime: Yup.string().required("Start time is required"),
  endTime: Yup.string().required("End time is required"),
  time_zone: Yup.string().required("Time zone is required"),

  // These are booleans for checkboxes
  eventInformation: Yup.boolean().oneOf([true], "Event information is required"),
  duplicateParticipants: Yup.boolean(),
  duplicateQuestions: Yup.boolean(),
  duplicateTickets: Yup.boolean()
});

const DuplicateEvent = () => {
  const { isOpen, onClose, type, data } = useModal();
  const { duplicateEventData, callback } = data;
  const { eventId, startDate, endDate, startTime, endTime, time_zone } = duplicateEventData || {};
  const initialValues = {
    startDate: new Date(), // Default to current date
    endDate: new Date(), // Default to current date
    startTime: "", // e.g., "06:00 PM"
    endTime: "", // e.g., "06:00 PM"
    time_zone: "", // e.g., "(GMT+5:30) Asia/Kolkata(IST)"
    eventInformation: true, // Default: selected
    duplicateParticipants: false,
    duplicateQuestions: true,
    duplicateTickets: false,
    start_epoc: "",
    end_epoc: "",
    eventId: ""
  };
  const { startTimeOptions: _startTimeOptions } = getStartTimeOpions(new Date(), 30, "");
  const [startTimeOptions, setStartTimeOptions] = useState(_startTimeOptions);
  const [endTimeOptions, setEndTimeOptions] = useState(_startTimeOptions);
  const [sDateModal, setSDateModal] = useState<boolean>(false);
  const [eDateModal, setEDateModal] = useState<boolean>(false);
  const [eventTime, setEventTime] = useState<string>("");
  const [eventDate, setEventDate] = useState<Date>(new Date());
  const { isMobile } = useDevice();

  useEffect(() => {
    const { startTime, startTimeOptions } = getStartTimeOpions(
      formik.values.startDate,
      30,
      formik.values.startTime
    );

    const { endTime, endTimeOptions } = getendTimeOpions(
      formik.values.startDate,
      formik.values.endDate,
      formik.values.startTime,
      formik.values.endTime,
      30
    );

    handleStart(new Date(), "");
    handleEnd(new Date(), new Date(), startTime);
  }, []);

  const handleEnd = (startDate: any, endDate: any, startTime: any) => {
    let endTimeMinutes = parseInt(startTime) + 60;
    // logic for handling edge case of 11 and 11.30 PM time
    if (endTimeMinutes >= 24 * 60) {
      const newEndDate = new Date(startDate);
      newEndDate.setDate(startDate.getDate() + 1);
      formik.setFieldValue("endDate", newEndDate);
      // endTimeMinutes -= 24 * 60; // This adjusts the endTime to wrap around to the next day
      const { endTime, endTimeOptions } = getendTimeOpions(
        startDate,
        newEndDate,
        startTime,
        formik.values.endTime,
        30
      );
      setEndTimeOptions(endTimeOptions);
      formik.setFieldValue("endTime", endTime);
      // const { endDate } = formik.values;
      const midnightEnd = dayjs(newEndDate).startOf("day");
      const targetTimeEnd = midnightEnd.add(Number(endTime), "minute");
      const unixTimestampEnd = targetTimeEnd.unix();
      formik.setFieldValue("end_epoc", unixTimestampEnd);
      return;
    }
    const { endTime, endTimeOptions } = getendTimeOpions(
      startDate,
      endDate,
      startTime,
      formik.values.endTime,
      30
    );
    setEndTimeOptions(endTimeOptions);
    formik.setFieldValue("endTime", endTime);
    // const { endDate } = formik.values;
    const midnightEnd = dayjs(endDate).startOf("day");
    const targetTimeEnd = midnightEnd.add(Number(endTime), "minute");
    const unixTimestampEnd = targetTimeEnd.unix();
    formik.setFieldValue("end_epoc", unixTimestampEnd);
  };

  const handleEnd1 = (startDate: any, endDate: any, startTime: any, eTimeValue: any) => {
    let endTimeMinutes = parseInt(startTime) + 60;
    // logic for handling edge case of 11 and 11.30 PM time
    if (endTimeMinutes >= 24 * 60) {
      const newEndDate = new Date(formik.values.startDate);
      newEndDate.setDate(formik.values.startDate.getDate() + 1);
      formik.setFieldValue("endDate", newEndDate);
      // endTimeMinutes -= 24 * 60; // This adjusts the endTime to wrap around to the next day
      const { endTime, endTimeOptions } = getendTimeOpions(
        startDate,
        endDate,
        startTime,
        formik.values.endTime,
        30
      );
      setEndTimeOptions(endTimeOptions);
      formik.setFieldValue("endTime", eTimeValue);
      // const { endDate } = formik.values;
      const midnightEnd = dayjs(newEndDate).startOf("day");
      const targetTimeEnd = midnightEnd.add(Number(endTime), "minute");
      const unixTimestampEnd = targetTimeEnd.unix();
      formik.setFieldValue("end_epoc", unixTimestampEnd);
      return;
    }
    const { endTime, endTimeOptions } = getendTimeOpions(
      startDate,
      endDate,
      startTime,
      eTimeValue,
      30
    );
    setEndTimeOptions(endTimeOptions);
    formik.setFieldValue("endTime", endTime);
    // const { endDate } = formik.values;
    const midnightEnd = dayjs(endDate).startOf("day");
    const targetTimeEnd = midnightEnd.add(Number(endTime), "minute");
    const unixTimestampEnd = targetTimeEnd.unix();
    formik.setFieldValue("end_epoc", unixTimestampEnd);
  };

  const handleEnd2 = (startDate: any, endDate: any, startTime: any, eTimeValue: any) => {
    let endTimeMinutes = parseInt(startTime) + 60;
    // logic for handling edge case of 11 and 11.30 PM time
    const { endTime, endTimeOptions } = getendTimeOpions(
      startDate,
      endDate,
      startTime,
      eTimeValue,
      30
    );
    setEndTimeOptions(endTimeOptions);
    formik.setFieldValue("endTime", eTimeValue);
    // const { endDate } = formik.values;
    const midnightEnd = dayjs(endDate).startOf("day");
    const targetTimeEnd = midnightEnd.add(Number(eTimeValue), "minute");
    const unixTimestampEnd = targetTimeEnd.unix();
    formik.setFieldValue("end_epoc", unixTimestampEnd);
  };

  const handleStart = (startDate: any, sTimeValue: any) => {
    const { startTime, startTimeOptions } = getStartTimeOpions(
      startDate,
      30,
      sTimeValue ? sTimeValue : formik.values.startTime
    );
    setStartTimeOptions(startTimeOptions);
    formik.setFieldValue("startTime", startTime);
    // formik.setFieldValue("startTime", startTime);
    // const { startDate } = formik.values;
    const midnightStart = dayjs(startDate).startOf("day");
    const targetTimeStart = midnightStart.add(Number(startTime), "minute");
    const unixTimestampStart = targetTimeStart.unix();
    formik.setFieldValue("start_epoc", unixTimestampStart);
  };
  const handleStart1 = (startDate: any, sTimeValue: any) => {
    const { startTime, startTimeOptions } = getStartTimeOpions(
      startDate,
      30,
      sTimeValue ? sTimeValue : formik.values.startTime
    );
    setStartTimeOptions(startTimeOptions);
    formik.setFieldValue("startTime", sTimeValue);
    // const { startDate } = formik.values;
    const midnightStart = dayjs(startDate).startOf("day");
    const targetTimeStart = midnightStart.add(Number(sTimeValue), "minute");
    const unixTimestampStart = targetTimeStart.unix();
    formik.setFieldValue("start_epoc", unixTimestampStart);
    handleEnd1(formik.values.startDate, formik.values.endDate, sTimeValue, formik.values.endTime);
  };

  const { data: options, isLoading, refetch } = useTimezones();

  const router = useRouter();

  const formik = useFormik({
    initialValues: initialValues,
    // validationSchema: validationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        let payload = {
          time_zone: values.time_zone,
          options: {
            duplicateParticipants: values.duplicateParticipants,
            duplicateQuestions: values.duplicateQuestions,
            duplicateTickets: values.duplicateTickets
          },
          start_epoc: values.start_epoc,
          end_epoc: values.end_epoc
        };
        const resp = await eventClientApi
          .post(`api/v1/events/${eventId}/duplicate`, { json: { ...payload } })
          .json<any>();
        if (resp?.status === 200) {
          toast.success(resp?.message || "Event duplicated successfully");
          router.push(`/events/manage/${resp.data.alias}?tab=info`);
        }
        callback && callback();
      } catch (error) {}
      resetForm();
      onClose();
    }
  });

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="h-full max-h-[500px] md:max-h-[730px] w-[90%] max-w-full-50px md:w-[600px] overflow-y-auto flex flex-col p-3 md:p-6">
        <DialogHeader className="sticky">
          <DialogTitle className="text-left">Duplicate event</DialogTitle>
        </DialogHeader>
        <Divider></Divider>

        <div className="flex  flex-col gap-[10px] w-full">
          <Text variant={"body_two_strong"} className="pb-2">
            Select new date and time for the upcoming event
          </Text>
          <div className="flex md:items-center flex-col md:flex-row w-full justify-between">
            <Text className="">Start date and time</Text>
            <div className="flex flex-row items-center gap-[10px] h-full md:px-xl">
              <div className="w-fit">
                <Popover open={sDateModal} onOpenChange={setSDateModal}>
                  <PopoverTrigger className="w-full">
                    <Input
                      fullWidth
                      className="text-sm"
                      value={dayjs(formik.values.startDate).format("ddd, MMM DD, YYYY")}
                    ></Input>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit">
                    <Calendar
                      mode="single"
                      name="startDate"
                      selected={formik.values.startDate}
                      onSelect={(e: string) => {
                        if (!e) return;
                        formik.setFieldValue("startDate", e);
                        // if (dayjs(formik.values.endDate).isBefore(dayjs(e))) {
                        //   formik.setFieldValue("endDate", e);
                        // }
                        if (dayjs(formik.values.endDate).isBefore(dayjs(e))) {
                          formik.setFieldValue("endDate", e);
                          handleStart(e, formik.values.startTime);
                          handleEnd(e, e, formik.values.startTime);
                        } else {
                          handleStart(e, formik.values.startTime);
                          handleEnd(e, formik.values.endDate, formik.values.startTime);
                        }
                        setSDateModal(false);
                      }}
                      disableDay={new Date()}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="w-fit">
                <Select
                  value={formik?.values?.startTime?.toString()}
                  onValueChange={(e) => {
                    handleStart1(formik.values.startDate, e);
                    // formik.setFieldValue("startTime", e);
                    // formik.setFieldValue("start_epoc", dayjs(e).unix());
                  }}
                >
                  <SelectTrigger
                    showChevrolate={false}
                    className="w-full font-normal  backdrop-blur-none"
                  >
                    <SelectValue placeholder="Select Time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {startTimeOptions &&
                        startTimeOptions.length > 0 &&
                        startTimeOptions.map((data) => (
                          <SelectItem value={data.value} key={data.value}>
                            {data.label}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center w-full justify-between">
            <Text className="w-fit">End date and time</Text>
            <div className="flex flex-row items-center gap-[10px] h-full md:px-xl">
              <div className="w-fit">
                <Popover open={eDateModal} onOpenChange={setEDateModal}>
                  <PopoverTrigger className="w-full">
                    <Input
                      fullWidth
                      value={dayjs(formik.values.endDate).format("ddd, MMM DD, YYYY")}
                      className="text-sm"
                    ></Input>
                  </PopoverTrigger>
                  <PopoverContent className="w-fit">
                    <Calendar
                      mode="single"
                      selected={formik.values.endDate}
                      onSelect={(e: string) => {
                        if (!e) return;
                        // formik.setFieldValue("endDate", e);
                        // formik.setFieldValue("end_epoc", dayjs(e).unix());
                        formik.setFieldValue("endDate", e);
                        handleEnd(formik.values.startDate, e, formik.values.startTime);
                        setEDateModal(false);
                      }}
                      disableDay={formik.values.startDate}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="fit">
                <Select
                  defaultValue={formik?.values?.endTime?.toString()}
                  value={formik?.values?.endTime?.toString()}
                  onValueChange={(e) =>
                    handleEnd2(
                      formik.values.startDate,
                      formik.values.endDate,
                      formik.values.startTime,
                      e
                    )
                  }
                >
                  <SelectTrigger
                    showChevrolate={false}
                    className="w-full font-normal  backdrop-blur-none"
                  >
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {endTimeOptions &&
                        endTimeOptions.length > 0 &&
                        endTimeOptions.map((data) => (
                          <SelectItem value={data.value} key={data.value}>
                            {data.label}
                          </SelectItem>
                        ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <div className="flex flex-col md:flex-row md:items-center w-full justify-between">
            <Text>Time zone</Text>
            <div className="flex flex-row items-center gap-0  md:mr-xl">
              {options && options.length > 0 && (
                <TimezoneSelectWithSearch
                  formik={formik}
                  options={options}
                  showGlobalIcon={false}
                  layperClasses={"!px-0 !py-0"}
                  isDuplicateEvent={true}
                />
              )}
              {/* <div className="w-3/5">
                <Input
                  variant="medium"
                  warpperClassName="!h-[100%]"
                  className="!rounded-s-4!rounded-e-none  w-full!h-[100%] px-xl"
                  placeholder="Select Date"
                  value={dayjs(new Date()).format("ddd, MMM DD, YYYY")}
                  unstyled
                />
              </div> */}
            </div>
          </div>
        </div>
        <Divider></Divider>

        <Text variant={"body_two_strong"}>
          The following details will be copied into the new event:
        </Text>

        <div className="flex items-center gap-s mr-xs">
          <Checkbox
            id="select-all"
            disabled={true}
            size="medium"
            checked={formik.values.eventInformation}
            onCheckedChange={(e: boolean) => {
              formik.setFieldValue("eventInformation", e);
            }}
          />
          <Text className="ml-1">Event information</Text>
        </div>
        <div className="flex items-center gap-s mr-xs">
          <Checkbox
            size="medium"
            id="select-all"
            disabled={true}
            checked={formik.values.duplicateQuestions}
            onCheckedChange={(e: boolean) => {
              formik.setFieldValue("duplicateQuestions", e);
            }}
          />
          <Text className="ml-1">Registration questions</Text>
        </div>
        <div className="flex items-center gap-s mr-xs">
          <Checkbox
            id="select-all"
            size="medium"
            checked={formik.values.duplicateParticipants}
            onCheckedChange={(e: boolean) => {
              formik.setFieldValue("duplicateParticipants", e);
            }}
          />
          <Text className="ml-1">Guest list</Text>
        </div>
        <div className="flex items-center gap-s mr-xs">
          <Checkbox
            size="medium"
            id="select-all"
            checked={formik.values.duplicateTickets}
            onCheckedChange={(e: boolean) => {
              formik.setFieldValue("duplicateTickets", e);
            }}
          />
          <Text className="ml-1">Tickets & Pricing</Text>
        </div>
        <Divider></Divider>
        <div className="flex items-start flex-col bg-[#cd33330f] rounded-md p-m-nudge border-1 border-color-[#cd333333]">
          <Text variant={"body_one_strong"}>Important Note:</Text>
          <Text variant={"body_one"}>
            Once you click Duplicate & Publish, the duplicated event will be published immediately.
            If you choose "Guest list" to duplicate, all guests from the original Guest List will
            automatically receive an invitation email for the new event.
          </Text>
        </div>

        <div className="flex justify-end gap-m mt-m">
          <Button
            className="md:w-1/3"
            variant="primary"
            onClick={() => formik.handleSubmit()}
            disabled={formik.isSubmitting}
            loading={formik.isSubmitting}
          >
            Duplicate & Publish
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DuplicateEvent;
